// src/components/workspace/SettingsSurface.tsx
// The two places a page's settings are shown, and the one description they are
// both built from.
//
// A setting is a section: a name, what is chosen right now, and the choices.
// The surface decides how much of that fits. In the navbar's menu a section is
// a row you drill into, because 264px of popover cannot hold five option lists
// at once; in the right rail it is a heading with its list underneath, because
// the panel has the height and the whole point of opening it is to see
// everything without hunting.
//
// Drilling down rather than nesting popovers is not a style choice: a second
// AnchoredMenu opened from inside the first portals to the body, and the outer
// menu's dismissal reads a click in it as a click outside itself. The open
// section renders in place of the root instead — no second popover exists.

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { observer } from '@react';
import { cn } from '@core/ui';

import { AnchoredMenu } from '@/components/AnchoredMenu';

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function SlidersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="21" x2="4" y2="14" />
      <line x1="4" y1="10" x2="4" y2="3" />
      <line x1="12" y1="21" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12" y2="3" />
      <line x1="20" y1="21" x2="20" y2="16" />
      <line x1="20" y1="12" x2="20" y2="3" />
      <line x1="1" y1="14" x2="7" y2="14" />
      <line x1="9" y1="8" x2="15" y2="8" />
      <line x1="17" y1="16" x2="23" y2="16" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Shared row styling — the menu and the panel look the same on purpose
// ---------------------------------------------------------------------------

export const settingsRowClass =
  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors';

export function SettingsLabel({ children }: { children: ReactNode }) {
  return <p className="px-2 pb-1 pt-2 text-xs font-medium text-muted-foreground">{children}</p>;
}

// ---------------------------------------------------------------------------
// Surface
// ---------------------------------------------------------------------------

interface SurfaceValue {
  variant: 'menu' | 'panel';
  /** Id of the section showing its choices, or null for the overview. */
  openId: string | null;
  setOpenId: (id: string | null) => void;
  /** Close the surface itself — for editors that finish in one action. */
  dismiss: () => void;
  /**
   * Suspends "a click outside closes me". A section whose editor is itself a
   * popover — the user picker is one — portals to the body, and a click in it
   * lands outside this menu's element. Escape, the trigger and the back row
   * still close the menu; only the outside click is given up, and only while
   * such a section is open.
   */
  setHoldsPopover: (holds: boolean) => void;
}

const SurfaceCtx = createContext<SurfaceValue | null>(null);

function useSurface(): SurfaceValue {
  const ctx = useContext(SurfaceCtx);
  if (!ctx) throw new Error('settings sections must render inside a <SettingsMenu> or <SettingsPanel>');
  return ctx;
}

/** Whether the surface is showing its overview, for callers that need to know. */
export function useSettingsSurface(): { variant: 'menu' | 'panel'; atRoot: boolean; dismiss: () => void } {
  const { variant, openId, dismiss } = useSurface();
  return { variant, atRoot: variant === 'panel' || openId === null, dismiss };
}

/**
 * One setting: its name, what is chosen, and the choices behind it.
 *
 * In a menu a section that is not the open one renders nothing at all while
 * another is open, which is what puts the open section — and its back row —
 * alone on the panel without anyone having to hold a stack of screens.
 */
export const SettingsSection = observer(({
  id,
  label,
  summary,
  icon,
  active,
  hostsPopover,
  children,
}: {
  /** Stable within one surface; it is what "which section is open" refers to. */
  id: string;
  label: string;
  /** What is chosen right now, read off the row without opening it. */
  summary?: ReactNode;
  icon?: ReactNode;
  /** Marks the section as narrowing the list — a dot rather than a summary. */
  active?: boolean;
  /** This section's editor opens a popover of its own. See `setHoldsPopover`. */
  hostsPopover?: boolean;
  children: ReactNode;
}) => {
  const { variant, openId, setOpenId, setHoldsPopover } = useSurface();

  const holds = variant === 'menu' && openId === id && !!hostsPopover;
  useEffect(() => {
    if (!holds) return;
    setHoldsPopover(true);
    return () => setHoldsPopover(false);
  }, [holds, setHoldsPopover]);

  if (variant === 'panel') {
    return (
      <>
        <SettingsLabel>{label}</SettingsLabel>
        {children}
      </>
    );
  }

  if (openId === id) {
    return (
      <>
        <button
          type="button"
          className={cn(settingsRowClass, 'text-xs font-medium text-muted-foreground hover:text-foreground')}
          onClick={() => setOpenId(null)}
        >
          <ChevronLeftIcon className="h-3 w-3 flex-shrink-0" />
          <span>{label}</span>
        </button>
        <div className="my-1 h-px bg-border" />
        {children}
      </>
    );
  }

  if (openId !== null) return null;

  return (
    <button
      type="button"
      role="menuitem"
      className={cn(settingsRowClass, 'text-foreground hover:bg-accent/50')}
      onClick={() => setOpenId(id)}
    >
      {icon}
      <span className="flex-1 truncate">{label}</span>
      {summary !== undefined && (
        <span className="max-w-[8rem] truncate text-xs text-muted-foreground">{summary}</span>
      )}
      {active && <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />}
      <ChevronRightIcon className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
    </button>
  );
});

SettingsSection.displayName = 'SettingsSection';

/**
 * Content that belongs to the overview only — a search box, a density toggle,
 * the reset row. A drilled-in menu shows one section and nothing else.
 */
export const SettingsRootOnly = observer(({ children }: { children: ReactNode }) => {
  const { variant, openId } = useSurface();
  if (variant === 'menu' && openId !== null) return null;
  return <>{children}</>;
});

SettingsRootOnly.displayName = 'SettingsRootOnly';

// ---------------------------------------------------------------------------
// The two surfaces
// ---------------------------------------------------------------------------

/**
 * The navbar's one button for everything the page can be set to.
 *
 * One button rather than a row of controls because these are all edits: you set
 * them once and read the result. What stays on screen is the state they
 * produced — the filter chips beside it.
 */
export const SettingsMenu = observer(({
  ariaLabel,
  badge,
  children,
}: {
  ariaLabel: string;
  /** How many things are narrowing the list right now. */
  badge?: number;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [holdsPopover, setHoldsPopover] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = () => {
    setOpen(false);
    setOpenId(null);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          setOpen((prev) => !prev);
          setOpenId(null);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        title={ariaLabel}
        className={cn(
          'flex h-7 flex-shrink-0 items-center gap-1.5 rounded-md border border-border px-2 text-xs font-medium transition-colors',
          open || (badge ?? 0) > 0
            ? 'bg-accent text-accent-foreground'
            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
        )}
      >
        <SlidersIcon className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="hidden sm:inline">{ariaLabel}</span>
        {(badge ?? 0) > 0 && (
          <span className="rounded-full bg-primary px-1.5 text-[10px] font-semibold leading-4 text-primary-foreground">
            {badge}
          </span>
        )}
      </button>

      <AnchoredMenu
        anchor={triggerRef}
        open={open}
        onClose={close}
        align="end"
        ariaLabel={ariaLabel}
        width={272}
        maxHeight={460}
        closeOnOutsideClick={!holdsPopover}
        className="p-1"
      >
        <SurfaceCtx.Provider
          value={{ variant: 'menu', openId, setOpenId, dismiss: close, setHoldsPopover }}
        >
          {children}
        </SurfaceCtx.Provider>
      </AnchoredMenu>
    </>
  );
});

SettingsMenu.displayName = 'SettingsMenu';

/** The same settings in the right rail, with nothing folded away. */
export function SettingsPanel({ children }: { children: ReactNode }) {
  return (
    <SurfaceCtx.Provider
      value={{
        variant: 'panel',
        openId: null,
        setOpenId: () => {},
        dismiss: () => {},
        setHoldsPopover: () => {},
      }}
    >
      {children}
    </SurfaceCtx.Provider>
  );
}
